import type { HelpPageContext } from '@accestra/types';

export interface ShellPageContext extends HelpPageContext {
  label: string;
  detail: string;
}

const UUID_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function extractUuidSegment(pathname: string, pattern: RegExp): string | null {
  const candidate = pathname.match(pattern)?.[1] ?? null;
  if (!candidate) {
    return null;
  }

  return UUID_SEGMENT_PATTERN.test(candidate)
    ? candidate
    : null;
}

function extractSiteCompliance(pathname: string): string | null {
  return extractUuidSegment(pathname, /^\/sites\/([^/]+)\/compliance$/u);
}

function extractSiteId(pathname: string): string | null {
  return extractUuidSegment(pathname, /^\/sites\/([^/]+)$/u);
}

function extractScanId(pathname: string): string | null {
  return extractUuidSegment(pathname, /^\/scans\/([^/]+)$/u);
}

export function getShellDocumentTitle(
  pathname: string,
  search: string,
  t: (key: string) => string,
): string {
  if (pathname.startsWith('/partner/referrals')) {
    return t('partnerPortal:shell.sections.referrals');
  }

  if (pathname.startsWith('/partner/commissions')) {
    return t('partnerPortal:shell.sections.commissions');
  }

  if (pathname.startsWith('/partner/payouts')) {
    return t('partnerPortal:shell.sections.payouts');
  }

  if (pathname.startsWith('/partner/settings')) {
    return t('partnerPortal:shell.sections.settings');
  }

  if (pathname.startsWith('/partner')) {
    return t('partnerPortal:shell.sections.dashboard');
  }

  return getShellPageContext(pathname, search, t).label;
}

export function getShellPageContext(
  pathname: string,
  search: string,
  t: (key: string) => string,
): ShellPageContext {
  const params = new URLSearchParams(search);
  const complianceSiteId = extractSiteCompliance(pathname);
  if (complianceSiteId) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.reports.label'),
      pageKey: 'site_compliance_report',
      siteId: complianceSiteId,
      reportId: null,
      label: t('shell.contexts.reports.label'),
      detail: t('shell.contexts.reports.detail'),
    };
  }

  const siteId = extractSiteId(pathname);
  if (siteId) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.sites.label'),
      pageKey: 'site_workspace',
      siteId,
      label: t('shell.contexts.sites.label'),
      detail: t('shell.contexts.sites.detail'),
    };
  }

  const scanId = extractScanId(pathname);
  if (scanId) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.issues.label'),
      pageKey: 'scan_detail',
      scanId,
      label: t('shell.contexts.issues.label'),
      detail: t('shell.contexts.issues.detail'),
    };
  }

  if (pathname.startsWith('/alerts')) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.alerts.label'),
      pageKey: 'alerts',
      label: t('shell.contexts.alerts.label'),
      detail: t('shell.contexts.alerts.detail'),
    };
  }

  if (pathname.startsWith('/billing')) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.billing.label'),
      pageKey: 'billing',
      label: t('shell.contexts.billing.label'),
      detail: t('shell.contexts.billing.detail'),
    };
  }

  if (pathname.startsWith('/workspace')) {
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.workspace.label'),
      pageKey: params.get('section') ? `workspace_${params.get('section')}` : 'workspace',
      label: t('shell.contexts.workspace.label'),
      detail: t('shell.contexts.workspace.detail'),
    };
  }

  if (pathname.startsWith('/partner')) {
    const section = pathname.replace(/^\/partner\/?/u, '') || 'dashboard';
    return {
      routePath: pathname,
      routeLabel: t('shell.contexts.partner.label'),
      pageKey: section === 'dashboard' ? 'partner_dashboard' : `partner_${section.replace(/\//gu, '_')}`,
      label: t('shell.contexts.partner.label'),
      detail: t('shell.contexts.partner.detail'),
    };
  }

  if (pathname.startsWith('/sites')) {
    return {
      routePath: `${pathname}${search}`,
      routeLabel: t('shell.contexts.sites.label'),
      pageKey: params.get('view') ? `sites_${params.get('view')}` : 'sites',
      label: t('shell.contexts.sites.label'),
      detail: t('shell.contexts.sites.collectionDetail'),
    };
  }

  return {
    routePath: pathname,
    routeLabel: t('shell.contexts.dashboard.label'),
    pageKey: 'dashboard',
    label: t('shell.contexts.dashboard.label'),
    detail: t('shell.contexts.dashboard.detail'),
  };
}
